import { Network } from 'vis-network';
import { DataSet } from 'vis-data';
import axios from 'axios';

/**
 * Initializes the Cognitive Galaxy visualization.
 * Supports both standalone page and tabbed view.
 */
export function initializeGalaxy() {
    const container = document.getElementById('galaxy-network');
    if (!container) return;

    // Check if we are in a tabbed view (Pro Version)
    const galaxyTab = document.getElementById('galaxy-tab');
    if (galaxyTab) {
        let network = null;
        let dataLoaded = false;

        galaxyTab.addEventListener('shown.bs.tab', function () {
            if (!dataLoaded) {
                loadGalaxyData(container, (net) => {
                    network = net;
                    dataLoaded = true;
                });
            } else if (network) {
                network.fit();
            }
        });
    } else {
        // Standalone page
        loadGalaxyData(container);
    }
}

/**
 * Loads galaxy data and renders the network.
 * @param {HTMLElement} container 
 * @param {Function} callback Optional callback receiving the network instance.
 */
function loadGalaxyData(container, callback) {
    const statusDiv = document.getElementById('galaxy-status');
    if (statusDiv) statusDiv.innerText = "Scanning your knowledge galaxy...";

    axios.get('/api/galaxy')
        .then(response => {
            const data = response.data;
            if (!data.nodes || data.nodes.length === 0) {
                container.innerHTML = '<div class="alert alert-info m-5 text-center">Your galaxy is still expanding. Keep chatting to map your knowledge!</div>';
                if (statusDiv) statusDiv.innerText = "";
                if (callback) callback(null);
                return;
            }

            // Theme-aware colors from style.css variables or defaults
            const colors = {
                primary: '#3A6351',
                primaryLight: '#88A793',
                primaryDark: '#2C4A3B',
                accent: '#C17817',
                textDark: '#2A2826'
            };

            const nodes = new DataSet(data.nodes.map(n => ({
                id: n.id,
                label: n.label,
                title: n.domain,
                group: n.domain,
                shape: 'dot',
                size: 25,
                font: { size: 16, color: colors.textDark },
                color: {
                    background: colors.primaryLight,
                    border: colors.primaryDark,
                    highlight: { background: colors.accent, border: colors.primaryDark }
                }
            })));

            const edges = new DataSet(data.links.map(e => ({
                from: e.from,
                to: e.to,
                width: Math.max(1, (e.weight || 0.1) * 5),
                label: e.type,
                title: e.type,
                arrows: 'to',
                color: { inherit: 'from', opacity: 0.6 },
                font: { align: 'middle', size: 10, color: colors.accent }
            })));

            const options = {
                physics: {
                    stabilization: true,
                    barnesHut: {
                        gravitationalConstant: -2000,
                        centralGravity: 0.3,
                        springLength: 120,
                        springConstant: 0.04,
                        damping: 0.09,
                        avoidOverlap: 0
                    }
                },
                nodes: {
                    borderWidth: 1,
                    shadow: true
                },
                edges: {
                    shadow: false,
                    smooth: {
                        type: 'continuous'
                    }
                },
                interaction: {
                    hover: true,
                    tooltipDelay: 200
                }
            };

            const network = new Network(container, { nodes, edges }, options);
            if (statusDiv) statusDiv.innerText = "";
            if (callback) callback(network);
        })
        .catch(err => {
            console.error('Error loading galaxy:', err);
            container.innerHTML = '<div class="alert alert-danger m-5">The galaxy is currently obscured. Please try again later.</div>';
            if (statusDiv) statusDiv.innerText = "";
            if (callback) callback(null);
        });
}
